import { levelScore, starsFor, type Round3Verse } from "@versedrill/core";

/**
 * Pure state machine for one game. All I/O lives in useGame; this file only decides what the
 * ladder of levels, the score and the hints look like after each event — so it's easy to test.
 */

export interface Choice {
  title: string;
  description?: string;
  /** Small print under the description, e.g. the verse range "12:1–23:20". */
  meta?: string;
}

export interface Level {
  id: string;
  round: 1 | 2 | 3;
  /** Path of already-correct indices leading to this level (rounds 1 and 2). */
  prefix: number[];
  choices: Choice[];
  /** Wrong guesses and which way the answer lies. */
  wrong: Record<number, "above" | "below">;
  correct: number | null;
  hinted: number[];
  /** Points this level earned once solved. */
  earned: number | null;
}

export interface GameState {
  started: boolean;
  levels: Level[];
  score: number;
  possible: number;
  hintsLeft: number;
  bookPath: number[];
  bookId: number | null;
  bookName: string | null;
  sectionPath: number[];
  slotVerses: Round3Verse[];
  redactionOn: boolean;
  phase: "idle" | "playing" | "finishing" | "finished";
}

export const initialState = (): GameState => ({
  started: false,
  levels: [],
  score: 0,
  possible: 0,
  hintsLeft: 0,
  bookPath: [],
  bookId: null,
  bookName: null,
  sectionPath: [],
  slotVerses: [],
  redactionOn: true,
  phase: "idle",
});

export type Action =
  | { type: "begin"; hints: number; redactable: boolean; first: Level }
  | { type: "wrong"; level: number; index: number; direction: "above" | "below" }
  | { type: "correct"; level: number; index: number }
  | { type: "append"; level: Level }
  | { type: "hint"; level: number; highlighted: number[] }
  | { type: "book"; bookId: number; bookName: string; bookPath: number[] }
  | { type: "section"; sectionPath: number[] }
  | { type: "slotVerses"; verses: Round3Verse[] }
  | { type: "finishing" }
  | { type: "finished" }
  | { type: "reveal" };

export function newLevel(round: 1 | 2 | 3, prefix: number[], choices: Choice[]): Level {
  return { id: `r${round}:${prefix.join(".")}`, round, prefix, choices, wrong: {}, correct: null, hinted: [], earned: null };
}

const patch = (levels: Level[], i: number, f: (l: Level) => Level): Level[] => levels.map((l, j) => (j === i ? f(l) : l));

export function reduce(state: GameState, a: Action): GameState {
  switch (a.type) {
    case "begin":
      return { ...initialState(), started: true, hintsLeft: a.hints, redactionOn: a.redactable, levels: [a.first], phase: "playing" };

    case "wrong":
      return { ...state, levels: patch(state.levels, a.level, (l) => ({ ...l, wrong: { ...l.wrong, [a.index]: a.direction } })) };

    case "correct": {
      const level = state.levels[a.level];
      if (!level || level.correct !== null) return state;
      const guessesUsed = Object.keys(level.wrong).length + 1;
      const s = levelScore(level.choices.length, guessesUsed);
      return {
        ...state,
        score: state.score + s.points,
        possible: state.possible + s.possiblePoints,
        levels: patch(state.levels, a.level, (l) => ({ ...l, correct: a.index, earned: s.points })),
      };
    }

    case "append":
      return state.levels.some((l) => l.id === a.level.id) ? state : { ...state, levels: [...state.levels, a.level] };

    case "hint": {
      if (state.hintsLeft <= 0) return state;
      return { ...state, hintsLeft: state.hintsLeft - 1, levels: patch(state.levels, a.level, (l) => ({ ...l, hinted: a.highlighted })) };
    }

    case "book":
      return { ...state, bookId: a.bookId, bookName: a.bookName, bookPath: a.bookPath };
    case "section":
      return { ...state, sectionPath: a.sectionPath };
    case "slotVerses":
      return { ...state, slotVerses: a.verses };
    case "finishing":
      return { ...state, phase: "finishing" };
    case "finished":
      return { ...state, phase: "finished" };
    case "reveal":
      return { ...state, redactionOn: false };
  }
}

export const currentLevelIndex = (s: GameState): number => s.levels.findIndex((l) => l.correct === null);
export const mistakes = (s: GameState): number => s.levels.reduce((n, l) => n + Object.keys(l.wrong).length, 0);
export const stars = (s: GameState): number => starsFor(s.score, s.possible);

/** Difficulty shown to the player: revealing the names removes the redaction bump. */
export function shownDifficulty(base: 0 | 1 | 2 | 3, redactable: boolean, redactionOn: boolean): 0 | 1 | 2 | 3 {
  return redactable && redactionOn ? (Math.min(base + 1, 3) as 0 | 1 | 2 | 3) : base;
}

/** Are names still inked out? Only while the puzzle is live: once it is solved there is nothing left to hide. */
export const blotsVisible = (redactionOn: boolean, solved: boolean): boolean => redactionOn && !solved;

/**
 * The verse text for the card at the top and the "Your verse" slot. Redacted while playing; the real text after the
 * player used "Reveal names" or solved the puzzle (`solvedText` is the reveal's text). This is for the screen only:
 * anything that leaves the app (the share text) must use the redacted text and never this.
 */
export function displayText(i: { redacted: string; unredacted: string | null; redactionOn: boolean; solvedText: string | null }): string {
  if (i.solvedText !== null) return i.solvedText;
  return i.redactionOn ? i.redacted : (i.unredacted ?? i.redacted);
}

/** Which option state to paint. Solved levels show only the correct pick; guessed options are struck. */
export function optionState(level: Level, i: number): "correct" | "wrong" | "hinted" | "idle" {
  if (level.correct === i) return "correct";
  if (i in level.wrong) return "wrong";
  if (level.correct === null && level.hinted.includes(i)) return "hinted";
  return "idle";
}
